import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Logo } from "@/components/layout/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, MessagesSquare, FileText, Mail, Gauge, Compass,
  Heart, FolderOpen, LogOut, User, GraduationCap, Rocket, ShieldCheck, X,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/agent", label: "AI Advisor", icon: MessagesSquare },
  { to: "/tools/cv", label: "CV Writer", icon: FileText },
  { to: "/tools/cover-letter", label: "Cover Letter", icon: Mail },
  { to: "/tools/predictor", label: "Admission Predictor", icon: Gauge },
  { to: "/tools/orientation", label: "Orientation Test", icon: Compass },
  { to: "/favorites", label: "Favorites", icon: Heart },
  { to: "/documents", label: "My Documents", icon: FolderOpen },
];

export function AppLayout({ children }) {
  const { user, logout: doLogout } = useAuth();
  const logout = async () => { await doLogout(); navigate("/login", { replace: true }); };
  const navigate = useNavigate();
  const PersonaIcon = user?.persona === "postgraduate" ? Rocket : GraduationCap;
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/banner").then(({ data }) => {
      const b = data.banner;
      if (b && localStorage.getItem("banner_dismissed") !== b.version) setBanner(b);
      else setBanner(null);
    }).catch(() => {});
  }, [user]);

  const bannerRef = useRef(null);
  useEffect(() => {
    // expose the banner height so full-height pages (chat) can subtract it
    const el = bannerRef.current;
    const root = document.documentElement.style;
    if (!el) { root.setProperty("--banner-h", "0px"); return undefined; }
    let frame = 0;
    let last = -1;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const h = Math.round(el.offsetHeight);
        if (h !== last) { last = h; root.setProperty("--banner-h", `${h}px`); }
      });
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); root.setProperty("--banner-h", "0px"); };
  }, [banner]);

  const dismissBanner = () => {
    if (banner) localStorage.setItem("banner_dismissed", banner.version);
    setBanner(null);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-navy text-white sticky top-0 h-screen">
        <div className="px-5 h-16 flex items-center border-b border-white/10">
          <Logo light />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`side-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? "bg-orange text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                {item.label}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="side-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive ? "bg-orange text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <ShieldCheck className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              Admin
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => navigate("/profile")}
            data-testid="side-profile"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/10 transition-colors text-left"
          >
            <div className="h-9 w-9 rounded-full bg-orange flex items-center justify-center shrink-0">
              <PersonaIcon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">{user?.persona}</div>
            </div>
          </button>
          <button
            onClick={logout}
            data-testid="side-logout"
            className="w-full mt-1 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/60 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-navy text-white h-16 flex items-center justify-between px-4">
          <Logo light />
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/profile")} data-testid="mobile-profile" className="h-9 w-9 rounded-full bg-orange flex items-center justify-center">
              <User className="h-4 w-4" />
            </button>
            <button onClick={logout} data-testid="mobile-logout" className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-border flex justify-around py-2">
          {NAV.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-orange" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" />
              </NavLink>
            );
          })}
        </nav>

        <main className="flex-1 min-w-0 pb-20 lg:pb-0">
          {banner && (
            <div ref={bannerRef} data-testid="notification-banner" className="bg-orange/10 border-b border-orange/30 px-5 py-3 flex items-start gap-3">
              <div
                className="flex-1 text-sm text-navy [&_a]:text-orange [&_a]:underline [&_a]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.html, { ADD_ATTR: ["target", "rel"] }) }}
              />
              <button onClick={dismissBanner} data-testid="banner-dismiss" className="text-navy/40 hover:text-navy shrink-0 mt-0.5">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
